import { router } from '@inertiajs/react'
import { RefreshCw, Trash2, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'

interface CarrierOption { id: string; name: string; code: string | null }

interface RateSheetDetail {
  id: string
  original_filename: string
  carrier_id: string | null
  carrier_name: string | null
  parse_format: string | null
  parse_status: string
  parse_error: string | null
  row_count: number
  effective_date: string | null
  expiry_date: string | null
  sender_email: string | null
  source: string
  notes: string | null
  created_at: string
}

interface Entry {
  id: string
  rate_type: string
  container_type: string
  rate_usd: number
  currency: string
  trade_scope: string | null
  commodity: string | null
  contract_reference: string | null
  origin: string | null
  pol: string | null
  pod: string | null
  destination: string | null
  effective_date: string | null
  expiry_date: string | null
}

interface Props {
  rate_sheet: RateSheetDetail
  entries: Entry[]
  entries_total: number
  entry_preview_limit: number
  carriers: CarrierOption[]
}

const STATUS_STYLES: Record<string, string> = {
  parsed: 'bg-green-100 text-green-700',
  parsing: 'bg-blue-100 text-blue-700',
  pending: 'bg-gray-100 text-gray-600',
  needs_review: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-700',
}

function money(value: number, currency: string): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: currency || 'USD' })
}

export default function RateSheetShow({ rate_sheet, entries, entries_total, entry_preview_limit, carriers }: Props) {
  const pathFor = useOrgPath()

  function assignCarrier(carrierId: string) {
    router.patch(pathFor(`/settings/rate_sheets/${rate_sheet.id}`), { rate_sheet: { carrier_id: carrierId } }, { preserveScroll: true })
  }

  function reparse() {
    router.post(pathFor(`/settings/rate_sheets/${rate_sheet.id}/reparse`), {}, { preserveScroll: true })
  }

  function destroy() {
    if (confirm('Delete this rate sheet and all its parsed rates?')) {
      router.delete(pathFor(`/settings/rate_sheets/${rate_sheet.id}`))
    }
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon-xs" onClick={() => router.visit(pathFor('/settings/rate_sheets'))} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-sm font-semibold">{rate_sheet.original_filename}</h1>
          <Badge variant="secondary" size="sm" className={`rounded-full font-medium ${STATUS_STYLES[rate_sheet.parse_status] || 'bg-gray-100'}`}>
            {rate_sheet.parse_status.replace('_', ' ')}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs" onClick={reparse}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Re-parse
          </Button>
          <Button variant="outline" size="sm" className="text-xs text-red-600" onClick={destroy}>
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-8">
        <section className="mb-6 grid max-w-3xl grid-cols-2 gap-4 text-[13px]">
          <div>
            <div className="mb-1 text-xs text-muted-foreground">Carrier</div>
            <select
              value={rate_sheet.carrier_id || ''}
              onChange={(event) => assignCarrier(event.target.value)}
              className="h-9 w-full rounded-md border border-border bg-white px-2 text-[13px]"
            >
              <option value="">Unassigned</option>
              {carriers.map((carrier) => (
                <option key={carrier.id} value={carrier.id}>{carrier.name}{carrier.code ? ` (${carrier.code})` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1 text-xs text-muted-foreground">Format</div>
            <div className="flex h-9 items-center">{rate_sheet.parse_format || '—'}</div>
          </div>
          <div>
            <div className="mb-1 text-xs text-muted-foreground">Validity</div>
            <div className="flex h-9 items-center">
              {rate_sheet.effective_date || rate_sheet.expiry_date
                ? `${rate_sheet.effective_date || '…'} → ${rate_sheet.expiry_date || '…'}`
                : '—'}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs text-muted-foreground">Parsed rows</div>
            <div className="flex h-9 items-center">{rate_sheet.row_count}</div>
          </div>
        </section>

        {rate_sheet.parse_status === 'failed' && rate_sheet.parse_error && (
          <div className="mb-6 max-w-3xl rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700">
            {rate_sheet.parse_error}
          </div>
        )}

        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Parsed rates</h2>
          {entries_total > entry_preview_limit && (
            <span className="text-xs text-muted-foreground">Showing {entry_preview_limit} of {entries_total}</span>
          )}
        </div>

        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-4 py-2 text-xs font-medium text-muted-foreground">Origin</th>
              <th className="px-4 py-2 text-xs font-medium text-muted-foreground">POL</th>
              <th className="px-4 py-2 text-xs font-medium text-muted-foreground">POD</th>
              <th className="px-4 py-2 text-xs font-medium text-muted-foreground">Destination</th>
              <th className="px-4 py-2 text-xs font-medium text-muted-foreground">Container</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Rate</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} className="border-b">
                <td className="px-4 py-2 text-[13px]">{entry.origin || '—'}</td>
                <td className="px-4 py-2 text-[13px]">{entry.pol || '—'}</td>
                <td className="px-4 py-2 text-[13px]">{entry.pod || '—'}</td>
                <td className="px-4 py-2 text-[13px]">{entry.destination || '—'}</td>
                <td className="px-4 py-2 text-[13px] font-mono">{entry.container_type}</td>
                <td className="px-4 py-2 text-right text-[13px] font-medium">{money(entry.rate_usd, entry.currency)}</td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No parsed rates yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

RateSheetShow.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
